import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Container, CheckTree, Popover, Progress } from 'rsuite'
import { useHotkeys } from 'react-hotkeys-hook'
import { ValueType } from 'rsuite/esm/CheckTree/CheckTree'
import useQuery from '@/client/utils/useQuery'
import ConverterListModel from '@/client/models/converter'
import InTableColumnModel from '@/client/models/inTableColumn'
import { PanelError } from '@/client/components/ErrorProvider'
import { Button } from '@/client/components/Controls'
import Loading from '@/client/components/Loading'
import { MessageBox } from '@/client/components/MessageBox'
import useProgress from '@/client/utils/useProgress'
import project from '@client/stores/projectStore'
import { useLayout } from '@client/components/Layout/LayoutManager.tsx'
import ConverterIssues from '@client/views/ConverterIssues.tsx'

type ConverterProps = { tab: any }
type ConvertProgressProps = { treeNodes: any; suspended: boolean; onFinish: () => void }
type ConvertMappingsProps = {
    treeNodes: any
    onCancel: () => void
    onDone: () => void
}
type CountDownProps = { counter: number }

const converterListModel = new ConverterListModel()
const inTableColumnModel = new InTableColumnModel()

const mappingProgressContentStyle = {
    height: '100%',
}
const mappingProgressStyle = {
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'flex-end',
    justifyContent: 'center',
    marginBottom: '1em',
}

const cancelMessage = 'Press ESC key to cancel'
const cancelHotkey = 'escape'

function ConvertProgress({ treeNodes, suspended, onFinish }: ConvertProgressProps) {
    const { t } = useTranslation()
    const [taskId, setTaskId] = useState<string>('')
    const { task, finished: taskFinished, failed, error } = useProgress(taskId)
    const [finished, setFinished] = useState<boolean>(false)
    // Kept once seen: the task that failed is dropped as soon as the next node
    // starts, and the message has to outlive it.
    const [failure, setFailure] = useState<string>('')
    const [nodePosition, setNodePosition] = useState<number>(0)
    const [currentNode, setCurrentNode] = useState<any>(null)

    // to prevent double processing
    const [isProcessing, setIsProcessing] = useState(false)
    const prevNodePositionRef = useRef<number>(-1)

    const calcedTreeNodes = useMemo(() => {
        return treeNodes.selected.map((selected: string) => {
            const [converterId, inTableId, fileId, fileName, sheetName, client] = selected.split('|')
            return { converterId, inTableId, fileId, fileName, sheetName, client }
        })
    }, [])
    const onePercent = useMemo(() => {
        if (calcedTreeNodes.length <= 0) return 0
        return 100 / calcedTreeNodes.length
    }, [calcedTreeNodes])
    const taskPosition = useMemo(() => {
        if (onePercent <= 0 || !task?.progress) return 0
        return (onePercent / 100) * task.progress
    }, [currentNode, task?.progress])
    const percent = useMemo(() => {
        if (calcedTreeNodes.length <= 0) return 0
        const percent = onePercent * nodePosition + taskPosition
        if (percent > 100) return 100
        return percent
    }, [nodePosition, taskPosition])

    // start mapping with the current node
    const processNextNode = async () => {
        if (!taskId && prevNodePositionRef.current == nodePosition && nodePosition < calcedTreeNodes.length) {
            setIsProcessing(true)
            const node = calcedTreeNodes[nodePosition]
            setCurrentNode(node)
            const result = await converterListModel.convert(project.currentProject.value?._id || '', node)
            if (result) setTaskId(result.data.taskId)
            else {
                // TODO: Error handling
                console.error('Could not load taskId')
            }
        } else {
            setFinished(true)
        }
    }

    // One file failing ends the run. Carrying on would arrive at "Mapping
    // finished" with that file silently missing from the result.
    useEffect(() => {
        if (!failed) return

        setFailure(error || t('Unknown error'))
        setFinished(true)
    }, [failed])

    // after finish the mapping, increase the calcTreeNodes node index
    useEffect(() => {
        if (failed) return

        if (taskFinished && isProcessing && taskId) {
            if (nodePosition < calcedTreeNodes.length) {
                setNodePosition((position) => position + 1)
                setCurrentNode(null)
                setTaskId('')
            } else {
                setFinished(true)
            }
        }
    }, [taskFinished])

    // load next element in datalist
    useEffect(() => {
        if (nodePosition < calcedTreeNodes.length) {
            if (nodePosition !== prevNodePositionRef.current) {
                prevNodePositionRef.current = nodePosition
                setIsProcessing(false)
                processNextNode()
            }
        } else {
            setFinished(true)
        }
    }, [currentNode, nodePosition])

    if (finished) {
        return (
            <Container className="center-in-parent">
                <span style={{ marginBottom: '2em' }}>{failure ? t('Mapping failed') : t('Mapping finished')}</span>
                {failure && <span style={{ marginBottom: '2em' }}>{failure}</span>}
                <Button appearance="ghost" onClick={onFinish}>
                    {t('Close')}
                </Button>
            </Container>
        )
    }

    return (
        <Container style={mappingProgressContentStyle}>
            <Container style={mappingProgressStyle}>
                {currentNode?.fileName} -&gt; {currentNode?.sheetName} ...
            </Container>
            <Progress
                percent={+percent.toFixed(0)}
                strokeColor="var(--special-color-1)"
                trailColor="var(--color-2)"
                showInfo={true}
            />
            <div className="cancel-message">{t(cancelMessage)}</div>
        </Container>
    )
}

function ConvertMappings({ treeNodes, onCancel, onDone }: ConvertMappingsProps) {
    const { t } = useTranslation()
    const [started, setStarted] = useState<boolean>(false)
    const [finished, setFinished] = useState<boolean>(false)
    const [canceled, setCanceled] = useState<boolean>(false)
    const [counter, setCounter] = useState<number>(5)

    useHotkeys(cancelHotkey, () => {
        setCanceled(true)
    })

    useEffect(() => {
        if (counter > 0) {
            const timer = setTimeout(() => {
                if (!canceled) {
                    const nextCounter = counter - 1
                    if (nextCounter === 0) setStarted(true)
                    setCounter(nextCounter)
                }
            }, 1000)
            return () => clearTimeout(timer)
        }
    }, [counter, canceled])

    const handleMessageboxButtons = (status: string) => {
        setCanceled(false)
        if (status === 'yes') {
            setStarted(false)
            onCancel()
        }
    }

    const buttons = [
        { key: 'yes', text: t('Yes') },
        { key: 'no', text: t('No') },
    ]

    const CountDown = ({ counter }: CountDownProps) => {
        const { t } = useTranslation()
        return (
            <div className="countdown center-in-parent">
                <div>{t('Mapping is starting in {seconds} seconds ...'.replace('{seconds}', `${counter}`))}</div>
                <div>{t(cancelMessage)}</div>
            </div>
        )
    }

    return (
        <>
            <Popover className="mapping-converter-popover overlay" visible>
                {!started ? (
                    <CountDown counter={counter} />
                ) : (
                    <ConvertProgress
                        treeNodes={treeNodes}
                        suspended={canceled}
                        onFinish={() => {
                            onDone()
                            onCancel()
                        }}
                    />
                )}
            </Popover>
            {canceled && !finished && (
                <MessageBox
                    message={t('Do you really want to cancel the mapping process?')}
                    buttons={buttons}
                    onClick={handleMessageboxButtons}
                />
            )}
        </>
    )
}

export default function Converter({ tab }: ConverterProps) {
    const { t } = useTranslation()
    const [disableStartButton, setDisableStartButton] = useState<boolean>(() => {
        return tab?.store?.treeNodes?.selected?.length == 0 || true
    })
    const [convertingStarted, setConvertingStarted] = useState<boolean>(false)
    const [refreshCounter, setRefreshCounter] = useState(0)

    const { data, loading, error } = useQuery(converterListModel.list, {
        args: {
            project: project.currentProject.value?._id,
            page: 1,
            count: 200,
            query: { intables: true },
        },
        allowEmptyVariables: false,
        refreshCounter,
    })
    const { addTab } = useLayout()

    const issuesController = {
        getTabContent({ tab }: any) {
            return <ConverterIssues tab={tab} />
        },

        getBreadCrumb(params: any) {
            const { tab } = params
            const { label } = tab.store || {}
            return [t('Converters'), t('Issues'), label || '']
        },
        useProjectInBreadCrumb() {
            return true
        },
    }

    const treeNodes = useMemo(() => {
        if (!data) {
            setDisableStartButton(tab?.store?.treeNodes?.selected?.length == 0)
            return tab?.store?.treeNodes || { items: [], selected: [] }
        }
        const selected: any[] = []
        const items: any[] = []

        data.forEach((item: any) => {
            // top-level (group) node

            const node: any = { label: `${item.name} (${item.outTableId})`, value: item._id, children: [] }
            item.inTables?.forEach((inTable: any) => {
                inTable.files?.forEach((file: any) => {
                    const { warnings = 0, errors = 0 } = item.issuesByFile[file._id] || { warnings: 0, errors: 0 }

                    const value = `${item._id}|${inTable._id}|${file._id}|${file.fileName}|${file.sheetName}|${file.client}`
                    node.children.push({
                        inTableId: inTable._id,
                        fileId: file._id,
                        label: `${file.fileName} - ${file.sheetName}`,
                        value,
                        parent: node,
                        warnings,
                        errors,
                        outTableId: item.outTableId,
                    } as never)

                    if (file.useOnMapping) selected.push(value)
                })
            })

            items.push(node as never)
        })

        tab.store.treeNodes = { items, selected }
        setDisableStartButton(selected.length == 0)
        return tab.store.treeNodes
    }, [data])

    if (error) return <PanelError message={error.message} />
    if (loading) return <Loading />

    // on click save checkbox state immediately to database
    function onSelect(item: any, value: ValueType, event: any) {
        const list: string[] = []
        const selected: string[] = []
        const checked = item.check
        if (item.children) {
            // node cklicked
            item.children.forEach((child: any) => {
                list.push(`${child.inTableId}-${child.fileId}`)
                selected.push(child.value)
            })
        } else {
            // leaf clicked
            list.push(`${item.inTableId}-${item.fileId}`)
            selected.push(item.value)
        }

        if (checked) {
            // add checked
            tab.store.treeNodes.selected = [...tab.store.treeNodes.selected, ...selected]
        } else {
            // remove checked
            tab.store.treeNodes.selected = tab.store.treeNodes.selected.filter((item: string) => {
                return selected.indexOf(item) == -1
            })
        }

        setDisableStartButton(tab.store.treeNodes.selected.length == 0)
        inTableColumnModel.updateUseOnMapping(project.currentProject.value?._id || '', {
            ids: list,
            checked: checked,
        })
    }

    // start mapping
    function onStartConverter(): void {
        setConvertingStarted(true)
    }

    // cancel mapping
    function onCancel() {
        setConvertingStarted(false)
    }
    function openIssues(nodeData: any) {
        const projectId = project.currentProject.value?._id || ''
        const outTableId = nodeData.outTableId

        addTab({
            dataId: `converter-issues-${outTableId}-${nodeData.fileId}`,
            name: t('Issues'),
            component: 'converterIssues',
            controller: issuesController,
            store: { projectId, outTableId, label: nodeData.label, fileId: nodeData.fileId },
        })
    }
    function renderTreeNode(nodeData: any) {
        const isLeaf = !nodeData?.children || nodeData.children.length === 0
        const warn = isLeaf ? (nodeData.warnings ?? 0) : 0
        const err = isLeaf ? (nodeData.errors ?? 0) : 0
        const show = isLeaf && (warn > 0 || err > 0)

        const onBadgeClick: React.MouseEventHandler<HTMLSpanElement> = (e) => {
            e.preventDefault()
            e.stopPropagation()
            if (isLeaf) openIssues(nodeData)
        }

        return (
            <div className="treenode-item">
                <span className="treenode-label">
                    {nodeData.label}
                    {show && (
                        <span
                            onClick={onBadgeClick}
                            title={t('Open converter issues')}
                            style={{
                                marginLeft: 8,
                                display: 'inline-flex',
                                gap: 6,
                                alignItems: 'center',
                                cursor: 'pointer',
                            }}
                            className="badge-group"
                        >
                            {warn > 0 && (
                                <span title={t('Warnings')} className="badge badge--warn">
                                    {warn}
                                </span>
                            )}
                            {err > 0 && (
                                <span title={t('Errors')} className="badge badge--error">
                                    {err}
                                </span>
                            )}
                        </span>
                    )}
                </span>
            </div>
        )
    }

    const ConverterTop = () => {
        return (
            <Container className="mapping-converter-top">
                <span>{/* dummy */}</span>
                <Button appearance="ghost" disabled={disableStartButton} onClick={onStartConverter}>
                    {t('Start mapping')}
                </Button>
            </Container>
        )
    }

    return (
        <>
            <Container className="mapping-converter">
                <ConverterTop />
                <Container className="mapping-converter-content">
                    <CheckTree
                        defaultExpandAll
                        defaultValue={treeNodes?.selected}
                        data={treeNodes?.items}
                        onSelect={onSelect}
                        renderTreeNode={renderTreeNode}
                    />
                </Container>
            </Container>
            {convertingStarted && (
                <ConvertMappings
                    treeNodes={treeNodes}
                    onCancel={onCancel}
                    onDone={() => setRefreshCounter((n) => n + 1)}
                />
            )}{' '}
        </>
    )
}
